import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusCircle,
  Package,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Filter,
  Grid3x3,
  List,
  Folder,
  Package2,
  Edit,
  Trash2,
  Copy,
  ChevronLeft,
  ChevronRight,
  X,
  Save,
  AlertTriangle as AlertIcon,
  History,
  Warehouse,
  Coins,
  StickyNote,
} from 'lucide-react';
import styles from './ArticoliPage.module.css';
import articoliService, { ArticoloRequest, ArticoloFilters, MovimentoSummary } from '../../services/articoliService';
import categorieArticoliService, { CategoriaArticolo } from '../../services/categorieArticoliService';
import { Articolo } from '../../types';
import { ExportDropdown, ExportFormat } from '@/components/common/ExportDropdown';
import { exportToExcel, exportToPdf, ExportColumn, SummaryRow } from '@/utils/exports';

// Extended interface for display purposes (just adds categoria name)
interface ArticoloDisplay extends Articolo {
  categoriaNome?: string;
}

const formatEuro = (valore: number): string =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(valore);

export const ArticoliPage: React.FC = () => {
  // Data states
  const [articoli, setArticoli] = useState<Articolo[]>([]);
  const [categorieArticoli, setCategorieArticoli] = useState<CategoriaArticolo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI states
  const [showArticleFormModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMovimentiModal, setShowMovimentiModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Articolo | null>(null);
  const [movimentiTarget, setMovimentiTarget] = useState<Articolo | null>(null);
  const [movimenti, setMovimenti] = useState<MovimentoSummary[]>([]);
  const [movimentiLoading, setMovimentiLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [noteFilter, setNoteFilter] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('tutte');
  const [statoFilter, setStatoFilter] = useState('tutti');
  const [sortBy, setSortBy] = useState('nome-asc');

  // Form states
  const [editingArticolo, setEditingArticolo] = useState<Articolo | null>(null);
  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    descrizione: '',
    note: '',
    prezzoAcquisto: '',
    prezzoVendita: '',
    unitaMisura: 'pz',
    categoriaArticoloId: '',
    quantita: '',
    scortaMinima: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<{nome?: boolean; prezzoAcquisto?: boolean; categoriaArticoloId?: boolean; codice?: string}>({});
  const [exportLoading, setExportLoading] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Reload articoli when filters change
  useEffect(() => {
    loadArticoli();
  }, [searchTerm, categoriaFilter]);

  const loadData = async () => {
    await Promise.all([loadArticoli(), loadCategorieArticoli()]);
  };

  const loadArticoli = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: ArticoloFilters = {};
      if (searchTerm) filters.nome = searchTerm;
      if (categoriaFilter !== 'tutte') filters.categoriaArticoloId = parseInt(categoriaFilter);
      const data = await articoliService.getArticoli(filters);
      setArticoli(data);
    } catch (err) {
      console.error('Error loading articoli:', err);
      setError('Errore nel caricamento degli articoli');
    } finally {
      setLoading(false);
    }
  };

  const loadCategorieArticoli = async () => {
    try {
      const data = await categorieArticoliService.getCategorieArticoliActive();
      setCategorieArticoli(data);
    } catch (err) {
      console.error('Error loading categorie articoli:', err);
    }
  };

  // Transform articoli to display format (add categoria name)
  const articoliDisplay: ArticoloDisplay[] = useMemo(() => {
    return articoli.map((art) => {
      const categoria = categorieArticoli.find(c => c.id === art.categoriaArticoloId);

      return {
        ...art,
        categoriaNome: art.categoriaArticoloNome || categoria?.nome || 'N/A',
      };
    });
  }, [articoli, categorieArticoli]);

  // Filter and sort articles (client-side filtering for stato and sorting)
  const filteredArticoli = useMemo(() => {
    let result = articoliDisplay.filter(a => {
      if (statoFilter !== 'tutti' && a.stato !== statoFilter) {
        return false;
      }
      if (noteFilter && !(a.note || '').toLowerCase().includes(noteFilter.toLowerCase())) {
        return false;
      }
      return true;
    });

    result.sort((a, b) => {
      switch (sortBy) {
        case 'nome-asc':
          return a.nome.localeCompare(b.nome);
        case 'nome-desc':
          return b.nome.localeCompare(a.nome);
        case 'prezzo-asc':
          return a.prezzoAcquisto - b.prezzoAcquisto;
        case 'prezzo-desc':
          return b.prezzoAcquisto - a.prezzoAcquisto;
        default:
          return 0;
      }
    });

    return result;
  }, [articoliDisplay, statoFilter, sortBy, noteFilter]);

  // Stats based on all articles
  const stats = useMemo(() => ({
    totale: articoliDisplay.length,
    disponibili: articoliDisplay.filter(a => a.stato === 'disponibile').length,
    scorteBasse: articoliDisplay.filter(a => a.stato === 'scorte_basse').length,
    esauriti: articoliDisplay.filter(a => a.stato === 'esaurito').length,
    giacenza: articoliDisplay.reduce((sum, a) => sum + (a.quantita || 0), 0),
    valore: articoliDisplay.reduce((sum, a) => sum + ((a.prezzoAcquisto || 0) * (a.quantita || 0)), 0),
  }), [articoliDisplay]);

  // Categoria merceologica selezionata nel form: da qui l'articolo eredita il costo di gestione medio
  const categoriaSelezionata = useMemo(
    () => categorieArticoli.find(c => String(c.id) === formData.categoriaArticoloId),
    [categorieArticoli, formData.categoriaArticoloId]
  );

  // Riepilogo economico della cronologia movimenti dell'articolo selezionato
  const movimentiRiepilogo = useMemo(() => {
    const entrate = movimenti.filter(m => m.tipo === 'ENTRATA');
    const uscite = movimenti.filter(m => m.tipo === 'USCITA');
    const ricavi = entrate.reduce((sum, m) => sum + m.importo, 0);
    const acquisti = uscite.reduce((sum, m) => sum + m.importo, 0);
    const quantitaVenduta = entrate.reduce((sum, m) => sum + (m.quantita ?? 1), 0);
    const costoGestioneMedio = movimentiTarget?.costoGestioneMedio ?? 0;
    const costoGestione = costoGestioneMedio * quantitaVenduta;
    return {
      ricavi,
      acquisti,
      quantitaVenduta,
      costoGestioneMedio,
      costoGestione,
      bilancio: ricavi - acquisti,
      margineNetto: ricavi - acquisti - costoGestione,
      numeroEntrate: entrate.length,
      numeroUscite: uscite.length,
    };
  }, [movimenti, movimentiTarget]);

  const getStatoBadge = (stato: string) => {
    switch (stato) {
      case 'disponibile':
        return { className: `${styles.badge} ${styles.badgeSuccess}`, icon: CheckCircle, label: 'Disponibile' };
      case 'scorte_basse':
        return { className: `${styles.badge} ${styles.badgeWarning}`, icon: AlertTriangle, label: 'Scorte basse' };
      case 'esaurito':
        return { className: `${styles.badge} ${styles.badgeError}`, icon: XCircle, label: 'Esaurito' };
      default:
        return { className: styles.badge, icon: CheckCircle, label: 'N/A' };
    }
  };

  const handleDeleteClick = (articolo: Articolo) => {
    setDeleteTarget(articolo);
    setShowDeleteModal(true);
  };

  const handleViewMovimenti = async (articolo: Articolo) => {
    setMovimentiTarget(articolo);
    setShowMovimentiModal(true);
    setMovimentiLoading(true);
    try {
      const data = await articoliService.getMovimentiByArticolo(articolo.id);
      setMovimenti(data);
    } catch (err) {
      console.error('Error loading movimenti:', err);
      setMovimenti([]);
    } finally {
      setMovimentiLoading(false);
    }
  };

  const handleEditClick = (articolo: Articolo) => {
    setEditingArticolo(articolo);
    setFormData({
      codice: articolo.codice,
      nome: articolo.nome,
      descrizione: articolo.descrizione || '',
      note: articolo.note || '',
      prezzoAcquisto: articolo.prezzoAcquisto?.toString() || '',
      prezzoVendita: articolo.prezzoVendita?.toString() || '',
      unitaMisura: articolo.unitaMisura || 'pz',
      categoriaArticoloId: articolo.categoriaArticoloId?.toString() || '',
      quantita: articolo.quantita?.toString() || '',
      scortaMinima: articolo.scortaMinima?.toString() || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDuplicateClick = (articolo: Articolo) => {
    setEditingArticolo(null); // null means new article
    setFormData({
      codice: articolo.codice + '_COPIA',
      nome: articolo.nome + ' (Copia)',
      descrizione: articolo.descrizione || '',
      note: articolo.note || '',
      prezzoAcquisto: articolo.prezzoAcquisto?.toString() || '',
      prezzoVendita: articolo.prezzoVendita?.toString() || '',
      unitaMisura: articolo.unitaMisura || 'pz',
      categoriaArticoloId: articolo.categoriaArticoloId?.toString() || '',
      quantita: articolo.quantita?.toString() || '',
      scortaMinima: articolo.scortaMinima?.toString() || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      await articoliService.deleteArticolo(deleteTarget.id);
      await loadArticoli();
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err: unknown) {
      console.error('Error deleting articolo:', err);
      const axiosError = err as { response?: { data?: { message?: string } } };
      alert(axiosError?.response?.data?.message || 'Errore durante l\'eliminazione dell\'articolo');
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields (codice is now optional - auto-generated if empty)
    const errors: {nome?: boolean; prezzoAcquisto?: boolean; categoriaArticoloId?: boolean} = {};
    if (!formData.nome) errors.nome = true;
    if (!formData.prezzoAcquisto) errors.prezzoAcquisto = true;
    if (!formData.categoriaArticoloId) errors.categoriaArticoloId = true;

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setFormLoading(true);
      const request: ArticoloRequest = {
        codice: formData.codice || undefined,
        nome: formData.nome,
        descrizione: formData.descrizione || undefined,
        note: formData.note || undefined,
        prezzoAcquisto: formData.prezzoAcquisto ? parseFloat(formData.prezzoAcquisto) : undefined,
        prezzoVendita: formData.prezzoVendita ? parseFloat(formData.prezzoVendita) : undefined,
        unitaMisura: formData.unitaMisura || undefined,
        categoriaArticoloId: formData.categoriaArticoloId ? parseInt(formData.categoriaArticoloId) : undefined,
        attivo: true,
        quantita: formData.quantita ? parseFloat(formData.quantita) : undefined,
        scortaMinima: formData.scortaMinima ? parseFloat(formData.scortaMinima) : undefined,
      };

      if (editingArticolo) {
        await articoliService.updateArticolo(editingArticolo.id, request);
      } else {
        await articoliService.createArticolo(request);
      }
      await loadArticoli();

      // Reset form
      setFormData({
        codice: '',
        nome: '',
        descrizione: '',
        note: '',
        prezzoAcquisto: '',
        prezzoVendita: '',
        unitaMisura: 'pz',
        categoriaArticoloId: '',
        quantita: '',
        scortaMinima: '',
      });
      setEditingArticolo(null);
      setFormErrors({});
      setShowModal(false);
    } catch (err: unknown) {
      console.error('Error saving articolo:', err);
      // Check if it's a duplicate code error
      const axiosError = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosError?.response?.status === 400) {
        const message = axiosError.response.data?.message || '';
        if (message.includes('codice') && message.includes('Esiste già')) {
          setFormErrors(prev => ({ ...prev, codice: 'Esiste già un articolo con questo codice' }));
          return;
        }
      }
      alert('Errore durante il salvataggio dell\'articolo');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: field === 'codice' ? undefined : false }));
    }
  };

  const handleExport = async (format: ExportFormat) => {
    if (filteredArticoli.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    setExportLoading(true);
    try {
      const columns: ExportColumn[] = [
        { field: 'codice', header: 'Codice', type: 'text', width: 12 },
        { field: 'nome', header: 'Nome', type: 'text', width: 25 },
        { field: 'categoriaNome', header: 'Categoria', type: 'text', width: 15 },
        { field: 'note', header: 'Note', type: 'text', width: 30 },
        { field: 'prezzoAcquisto', header: 'Prezzo Acquisto', type: 'currency', width: 12 },
        { field: 'prezzoVendita', header: 'Prezzo Vendita', type: 'currency', width: 12 },
        { field: 'costoGestioneMedio', header: 'Costo Gestione Medio', type: 'currency', width: 15 },
        { field: 'quantita', header: 'Quantità', type: 'number', width: 10 },
        { field: 'unitaMisura', header: 'U.M.', type: 'text', width: 8 },
        { field: 'scortaMinima', header: 'Scorta Minima', type: 'number', width: 10 },
        { field: 'statoDisplay', header: 'Stato', type: 'text', width: 12 },
      ];

      const exportData = filteredArticoli.map(a => ({
        codice: a.codice || '',
        nome: a.nome,
        categoriaNome: a.categoriaNome || '',
        note: a.note || '',
        prezzoAcquisto: a.prezzoAcquisto || 0,
        prezzoVendita: a.prezzoVendita || 0,
        costoGestioneMedio: a.costoGestioneMedio || 0,
        quantita: a.quantita || 0,
        unitaMisura: a.unitaMisura || 'pz',
        scortaMinima: a.scortaMinima || 0,
        statoDisplay: a.stato === 'disponibile' ? 'Disponibile' : a.stato === 'scorte_basse' ? 'Scorte basse' : 'Esaurito',
      }));

      const summaryData: SummaryRow[] = [
        { label: 'Totale articoli', value: stats.totale, type: 'number' },
        { label: 'Valore magazzino', value: stats.valore, type: 'currency' },
      ];

      const config = {
        title: 'Articoli',
        filename: 'articoli',
        columns,
        orientation: 'landscape' as const,
        summaryData,
      };

      if (format === 'excel') {
        await exportToExcel(config, exportData);
      } else {
        await exportToPdf(config, exportData);
      }
    } catch (err) {
      console.error('Error exporting articoli:', err);
      setError('Errore durante l\'esportazione');
    } finally {
      setExportLoading(false);
    }
  };

  if (loading && articoli.length === 0) {
    return (
      <div className={styles.container}>
        <main>
          <div className={styles.pageHeader}>
            <h1 className={styles.pageTitle}>Magazzino</h1>
          </div>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            Caricamento...
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <main>
          <div className={styles.pageHeader}>
            <h1 className={styles.pageTitle}>Magazzino</h1>
          </div>
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--error-main)' }}>
            {error}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <main>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Magazzino</h1>
          <div className={styles.pageActions}>
            <ExportDropdown
              onExport={handleExport}
              disabled={filteredArticoli.length === 0}
              loading={exportLoading}
            />
            <button className={styles.btnPrimary} onClick={() => { setEditingArticolo(null); setFormData({ codice: '', nome: '', descrizione: '', note: '', prezzoAcquisto: '', prezzoVendita: '', unitaMisura: 'pz', categoriaArticoloId: '', quantita: '', scortaMinima: '' }); setFormErrors({}); setShowModal(true); }}>
              <PlusCircle size={18} />
              <span className={styles.btnLabel}>Nuovo</span>
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <Package size={20} style={{ color: 'var(--primary-main)' }} />
            <span className={styles.statNumber}>{stats.totale}</span>
            <span className={styles.statText}>Totale</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <CheckCircle size={20} style={{ color: 'var(--success-main)' }} />
            <span className={styles.statNumber}>{stats.disponibili}</span>
            <span className={styles.statText}>Disponibili</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <AlertTriangle size={20} style={{ color: 'var(--warning-main)' }} />
            <span className={styles.statNumber}>{stats.scorteBasse}</span>
            <span className={styles.statText}>Scorte basse</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <XCircle size={20} style={{ color: 'var(--error-main)' }} />
            <span className={styles.statNumber}>{stats.esauriti}</span>
            <span className={styles.statText}>Esauriti</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <Warehouse size={20} style={{ color: 'var(--info-main, #2196f3)' }} />
            <span className={styles.statNumber}>{stats.giacenza.toLocaleString('it-IT')}</span>
            <span className={styles.statText}>Giacenza</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <Coins size={20} style={{ color: 'var(--success-dark, #1b5e20)' }} />
            <span className={styles.statNumber}>€ {stats.valore.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
            <span className={styles.statText}>Valore</span>
          </div>
        </div>

        {/* Filters Card */}
        <div className={styles.filtersCard}>
          <div className={styles.filtersHeader}>
            <Filter size={16} />
            <span>Filtri e Ricerca</span>
          </div>
          <div className={styles.filtersGrid}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Cerca</label>
              <input
                type="text"
                placeholder="Nome articolo..."
                className={styles.formControl}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Note</label>
              <input
                type="text"
                placeholder="Cerca nelle note..."
                className={styles.formControl}
                value={noteFilter}
                onChange={(e) => setNoteFilter(e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Categoria</label>
              <select
                className={styles.formControl}
                value={categoriaFilter}
                onChange={(e) => setCategoriaFilter(e.target.value)}
              >
                <option value="tutte">Tutte</option>
                {categorieArticoli.map(cat => (
                  <option key={cat.id} value={cat.id.toString()}>{cat.nome}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Stato</label>
              <select
                className={styles.formControl}
                value={statoFilter}
                onChange={(e) => setStatoFilter(e.target.value)}
              >
                <option value="tutti">Tutti</option>
                <option value="disponibile">Disponibili</option>
                <option value="scorte_basse">Scorte basse</option>
                <option value="esaurito">Esauriti</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Ordina per</label>
              <select
                className={styles.formControl}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="nome-asc">Nome (A-Z)</option>
                <option value="nome-desc">Nome (Z-A)</option>
                <option value="prezzo-asc">Prezzo crescente</option>
                <option value="prezzo-desc">Prezzo decrescente</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Bar with View Toggle */}
        <div className={styles.resultsBar}>
          <div className={styles.resultsInfo}>
            <span>{filteredArticoli.length} articoli trovati</span>
          </div>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.btnIcon} ${viewMode === 'list' ? styles.active : ''}`}
              onClick={() => setViewMode('list')}
              title="Vista lista"
            >
              <List size={18} />
            </button>
            <button
              className={`${styles.btnIcon} ${viewMode === 'grid' ? styles.active : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vista griglia"
            >
              <Grid3x3 size={18} />
            </button>
          </div>
        </div>

        {/* LIST VIEW */}
        {viewMode === 'list' && (
          <div className={styles.tableCard}>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Articolo</th>
                    <th className={styles.hideOnMobile}>Categoria</th>
                    <th>Prezzo</th>
                    <th>Qtà</th>
                    <th>Stato</th>
                    <th className={styles.textRight}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArticoli.map((articolo) => {
                    const badge = getStatoBadge(articolo.stato);
                    const BadgeIcon = badge.icon;
                    return (
                      <tr key={articolo.id}>
                        <td>
                          <div className={styles.articleCell}>
                            <Package size={16} style={{ color: 'var(--primary-main)', flexShrink: 0 }} />
                            <div className={styles.articleInfo}>
                              <span className={styles.articleName}>{articolo.nome}</span>
                              {articolo.note && (
                                <span className={styles.articleNote} title={articolo.note}>
                                  <StickyNote size={11} />
                                  {articolo.note}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className={styles.hideOnMobile}>
                          <span className={styles.categoryTag}>{articolo.categoriaNome}</span>
                        </td>
                        <td className={styles.priceCell}>€ {articolo.prezzoAcquisto?.toFixed(2) || '0.00'}</td>
                        <td className={styles.qtyCell}>{articolo.quantita}</td>
                        <td>
                          <span className={badge.className}>
                            <BadgeIcon size={12} />
                            <span className={styles.hideOnMobile}>{badge.label}</span>
                          </span>
                        </td>
                        <td className={styles.textRight}>
                          <div className={styles.rowActions}>
                            <button className={styles.btnIcon} onClick={() => handleViewMovimenti(articolo)} title="Movimenti"><History size={16} /></button>
                            <button className={styles.btnIcon} onClick={() => handleEditClick(articolo)} title="Modifica"><Edit size={16} /></button>
                            <button className={styles.btnIcon} onClick={() => handleDuplicateClick(articolo)} title="Duplica"><Copy size={16} /></button>
                            <button className={styles.btnIcon} onClick={() => handleDeleteClick(articolo)} title="Elimina"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* GRID VIEW - Compact Cards */}
        {viewMode === 'grid' && (
          <div className={styles.articlesGrid}>
            {filteredArticoli.map((articolo) => {
              const badge = getStatoBadge(articolo.stato);
              const BadgeIcon = badge.icon;
              return (
                <div key={articolo.id} className={styles.articleCard}>
                  <div className={styles.cardTop}>
                    <div className={styles.cardIcon}>
                      <Package size={20} />
                    </div>
                    <span className={badge.className}>
                      <BadgeIcon size={12} />
                    </span>
                  </div>
                  <h4 className={styles.cardTitle}>{articolo.nome}</h4>
                  <div className={styles.cardCategory}>
                    <Folder size={12} />
                    {articolo.categoriaNome}
                  </div>
                  {articolo.note && (
                    <div className={styles.cardNote} title={articolo.note}>
                      <StickyNote size={12} />
                      <span>{articolo.note}</span>
                    </div>
                  )}
                  <div className={styles.cardFooter}>
                    <span className={styles.cardPrice}>€ {articolo.prezzoAcquisto?.toFixed(2) || '0.00'}</span>
                    <span className={styles.cardQty}>
                      <Package2 size={14} />
                      {articolo.quantita} pz
                    </span>
                  </div>
                  <div className={styles.cardActions}>
                    <button className={styles.btnIconSmall} onClick={() => handleViewMovimenti(articolo)} title="Movimenti"><History size={14} /></button>
                    <button className={styles.btnIconSmall} onClick={() => handleEditClick(articolo)} title="Modifica"><Edit size={14} /></button>
                    <button className={styles.btnIconSmall} onClick={() => handleDuplicateClick(articolo)} title="Duplica"><Copy size={14} /></button>
                    <button className={styles.btnIconSmall} onClick={() => handleDeleteClick(articolo)} title="Elimina"><Trash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>1-{filteredArticoli.length} di {filteredArticoli.length}</span>
          <div className={styles.paginationButtons}>
            <button className={styles.btnIcon} disabled><ChevronLeft size={18} /></button>
            <button className={styles.btnIcon}><ChevronRight size={18} /></button>
          </div>
        </div>
      </main>

      {/* Modal */}
      {showArticleFormModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleFormSubmit}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>{editingArticolo ? 'Modifica Articolo' : 'Nuovo Articolo'}</h3>
                <button type="button" className={styles.modalClose} onClick={() => { setShowModal(false); setEditingArticolo(null); }}>
                  <X size={20} />
                </button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Codice</label>
                  <input
                    type="text"
                    className={`${styles.formControl} ${formErrors.codice ? styles.error : ''}`}
                    placeholder="Lascia vuoto per generazione automatica"
                    value={formData.codice}
                    onChange={(e) => handleFormChange('codice', e.target.value)}
                  />
                  {formErrors.codice ? (
                    <span className={styles.errorText}>{formErrors.codice}</span>
                  ) : (
                    <span className={styles.helpText} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Se vuoto, verrà generato automaticamente</span>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Nome *</label>
                  <input
                    type="text"
                    className={`${styles.formControl} ${formErrors.nome ? styles.error : ''}`}
                    placeholder="Nome articolo"
                    value={formData.nome}
                    onChange={(e) => handleFormChange('nome', e.target.value)}
                  />
                  {formErrors.nome && <span className={styles.errorText}>Campo obbligatorio</span>}
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Descrizione</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    placeholder="Descrizione"
                    value={formData.descrizione}
                    onChange={(e) => handleFormChange('descrizione', e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Note</label>
                  <textarea
                    className={styles.formControl}
                    placeholder="Note (es. fornitore, scadenza, dettagli)"
                    rows={2}
                    value={formData.note}
                    onChange={(e) => handleFormChange('note', e.target.value)}
                  />
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Categoria *</label>
                    <select
                      className={`${styles.formControl} ${formErrors.categoriaArticoloId ? styles.error : ''}`}
                      value={formData.categoriaArticoloId}
                      onChange={(e) => { handleFormChange('categoriaArticoloId', e.target.value); setFormErrors(prev => ({ ...prev, categoriaArticoloId: false })); }}
                      disabled={categorieArticoli.length === 0}
                    >
                      <option value="">Seleziona...</option>
                      {categorieArticoli.map(cat => (
                        <option key={cat.id} value={cat.id.toString()}>{cat.nome}</option>
                      ))}
                    </select>
                    {categorieArticoli.length === 0 ? (
                      <span className={styles.errorText} style={{ color: 'var(--warning-main)', fontSize: '0.8rem' }}>
                        Nessuna categoria merceologica disponibile.{' '}
                        <Link to="/categorie-articoli" style={{ textDecoration: 'underline', fontWeight: 600 }}>Creane una</Link>
                        {' '}per poter salvare l'articolo.
                      </span>
                    ) : formErrors.categoriaArticoloId && (
                      <span className={styles.errorText} style={{ color: 'var(--error-main)', fontSize: '0.8rem' }}>La categoria è obbligatoria</span>
                    )}
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Prezzo Acquisto *</label>
                    <input
                      type="number"
                      className={`${styles.formControl} ${formErrors.prezzoAcquisto ? styles.error : ''}`}
                      placeholder="0.00"
                      step="0.01"
                      value={formData.prezzoAcquisto}
                      onChange={(e) => handleFormChange('prezzoAcquisto', e.target.value)}
                    />
                    {formErrors.prezzoAcquisto && <span className={styles.errorText}>Campo obbligatorio</span>}
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Prezzo Vendita</label>
                    <input
                      type="number"
                      className={styles.formControl}
                      placeholder="0.00"
                      step="0.01"
                      value={formData.prezzoVendita}
                      onChange={(e) => handleFormChange('prezzoVendita', e.target.value)}
                    />
                  </div>
                </div>
                {categoriaSelezionata && (
                  <div className={styles.inheritedCost}>
                    <Coins size={14} />
                    {categoriaSelezionata.costoGestioneMedio != null ? (
                      <span>
                        Costo di gestione medio ereditato dalla categoria:{' '}
                        <strong>{formatEuro(categoriaSelezionata.costoGestioneMedio)}</strong> per unità venduta
                      </span>
                    ) : (
                      <span>
                        Categoria senza costo di gestione medio:{' '}
                        <Link to="/categorie-articoli" style={{ textDecoration: 'underline' }}>impostalo in Merceologia</Link>
                      </span>
                    )}
                  </div>
                )}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Unità di Misura</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      placeholder="pz"
                      value={formData.unitaMisura}
                      onChange={(e) => handleFormChange('unitaMisura', e.target.value)}
                    />
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Quantità</label>
                    <input
                      type="number"
                      className={styles.formControl}
                      placeholder="0"
                      step="0.01"
                      value={formData.quantita}
                      onChange={(e) => handleFormChange('quantita', e.target.value)}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Scorta Minima</label>
                    <input
                      type="number"
                      className={styles.formControl}
                      placeholder="0"
                      step="0.01"
                      value={formData.scortaMinima}
                      onChange={(e) => handleFormChange('scortaMinima', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className={styles.btnSecondary} onClick={() => setShowModal(false)}>
                  Annulla
                </button>
                <button type="submit" className={styles.btnPrimary} disabled={formLoading}>
                  <Save size={16} /> {formLoading ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}>
          <div className={styles.modalConfirm} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <AlertIcon size={40} />
            </div>
            <h3 className={styles.confirmTitle}>Eliminare questo articolo?</h3>
            <p className={styles.confirmText}>
              Stai per eliminare <strong>{deleteTarget.nome}</strong>.
              Questa azione non può essere annullata.
            </p>
            {!!deleteTarget.numeroMovimenti && deleteTarget.numeroMovimenti > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', margin: '0 0 12px', borderRadius: '8px', background: 'var(--warning-light, #fff4e5)', color: 'var(--warning-dark, #663c00)', border: '1px solid var(--warning-main, #ff9800)', textAlign: 'left' }}>
                <AlertIcon size={18} />
                <span>Questo articolo è utilizzato in {deleteTarget.numeroMovimenti} movimenti/ordini e non può essere eliminato.</span>
              </div>
            )}
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setShowDeleteModal(false)}>
                Annulla
              </button>
              <button
                className={styles.btnDanger}
                onClick={handleConfirmDelete}
                disabled={!!deleteTarget.numeroMovimenti && deleteTarget.numeroMovimenti > 0}
              >
                <Trash2 size={16} />
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Movimenti Modal */}
      {showMovimentiModal && movimentiTarget && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowMovimentiModal(false); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <History size={20} style={{ marginRight: '8px' }} />
                Movimenti - {movimentiTarget.nome}
              </h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowMovimentiModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* Giacenza Info */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Giacenza attuale</span>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--primary-main)' }}>{movimentiTarget.quantita} {movimentiTarget.unitaMisura || 'pz'}</div>
                </div>
                {movimentiTarget.scortaMinima !== null && (
                  <div>
                    <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Scorta minima</span>
                    <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--warning-main)' }}>{movimentiTarget.scortaMinima} {movimentiTarget.unitaMisura || 'pz'}</div>
                  </div>
                )}
                <div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Prezzo unitario</span>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{movimentiTarget.prezzoAcquisto?.toFixed(2) || '0.00'}</div>
                </div>
                <div>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Costo gestione medio</span>
                  <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--warning-dark, #b26a00)' }}>
                    {movimentiTarget.costoGestioneMedio != null ? formatEuro(movimentiTarget.costoGestioneMedio) : '—'}
                  </div>
                </div>
              </div>

              {movimentiTarget.note && (
                <div className={styles.movimentiNote}>
                  <StickyNote size={16} />
                  <span>{movimentiTarget.note}</span>
                </div>
              )}

              {/* Movimenti Summary */}
              {movimentiLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>Caricamento...</div>
              ) : movimenti.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
                  Nessun movimento registrato per questo articolo
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Totale Entrate */}
                  <div style={{ padding: '16px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Totale Entrate (Vendite)</span>
                        <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--success-main)' }}>
                          + {formatEuro(movimentiRiepilogo.ricavi)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>N. movimenti</span>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                          {movimentiRiepilogo.numeroEntrate}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Totale Uscite */}
                  <div style={{ padding: '16px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Totale Uscite (Acquisti)</span>
                        <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--error-main)' }}>
                          - {formatEuro(movimentiRiepilogo.acquisti)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>N. movimenti</span>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                          {movimentiRiepilogo.numeroUscite}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Costo di gestione stimato, ereditato dalla categoria merceologica */}
                  <div style={{ padding: '16px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.25)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Costo di Gestione (stimato)</span>
                        <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--warning-dark, #b26a00)' }}>
                          - {formatEuro(movimentiRiepilogo.costoGestione)}
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {movimentiTarget.costoGestioneMedio != null
                            ? formatEuro(movimentiRiepilogo.costoGestioneMedio) + ' × ' + movimentiRiepilogo.quantitaVenduta.toLocaleString('it-IT') + ' ' + (movimentiTarget.unitaMisura || 'pz') + ' venduti'
                            : 'Costo di gestione medio non impostato sulla categoria merceologica'}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Qtà venduta</span>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                          {movimentiRiepilogo.quantitaVenduta.toLocaleString('it-IT')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Margine netto = entrate - uscite - costo di gestione */}
                  <div style={{ padding: '16px', background: 'var(--bg-tertiary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Margine Netto (stimato)</span>
                        <div style={{ fontSize: '1.5rem', fontWeight: 600, color: movimentiRiepilogo.margineNetto >= 0 ? 'var(--success-main)' : 'var(--error-main)' }}>
                          {formatEuro(movimentiRiepilogo.margineNetto)}
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          Entrate − Uscite − Costo di gestione · Bilancio movimenti {formatEuro(movimentiRiepilogo.bilancio)}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Totale movimenti</span>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                          {movimenti.length}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.btnSecondary} onClick={() => setShowMovimentiModal(false)}>
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
